import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button, Checkbox, EmptyState, Field, Modal, Notice, PageHeader, RowActions, Select, StatusIndicator, Table, Tag, TextArea } from '@voice/ui';
import { DestinationPicker, describeDestination, errorText, useDirectory } from '../components/common';
import { api } from '../lib/api';
import type { Destination, OutboundRules, TrunkNumber, TrunkView } from '../lib/types';
import styles from './Pages.module.css';

const KEY = ['trunks'] as const;

const STATE_LABEL: Record<string, string> = { registered: 'Angemeldet', registering: 'Meldet an …', failed: 'Fehler', disabled: 'Ausgeschaltet', 'not-required': 'Bereit' };

export function Trunks() {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: KEY, queryFn: () => api<{ trunks: TrunkView[]; publicHost: string | null }>('/trunks'), refetchInterval: 10_000 });
  const [editing, setEditing] = useState<TrunkView | 'new' | null>(null);
  const dir = useDirectory();

  return (
    <div className={styles.page}>
      <PageHeader title="Leitungen und Rufnummern">
        <Button onClick={() => setEditing('new')}>Anbieter hinzufügen</Button>
      </PageHeader>

      {query.isPending && <p className={styles.note}>Lädt …</p>}
      {query.isError && <Notice tone="error">Die Leitungen konnten nicht geladen werden.</Notice>}
      {query.data && query.data.trunks.length === 0 && <EmptyState>Noch kein Anbieter. Lege deinen easybell-Anschluss an – die Vorlage trägt Server, Port und Verschlüsselung für dich ein.</EmptyState>}

      {query.data?.trunks.map((t) => (
        <section key={t.id} className={styles.section}>
          <div className={styles.inline} style={{ justifyContent: 'space-between' }}>
            <div>
              <h2 className={styles.heading} style={{ marginBottom: 4 }}>
                {t.name}
              </h2>
              <div className={styles.inline}>
                <StatusIndicator state={t.status?.state === 'registered' || t.status?.state === 'not-required' ? 'ok' : t.status?.state === 'failed' ? 'busy' : 'off'} label={STATE_LABEL[t.status?.state ?? 'disabled'] ?? '–'} />
                <span className={styles.muted}>
                  {t.host}:{t.port} ({t.transport}) · {t.status?.channels.inUse ?? 0} von {t.maxChannels} Leitungen belegt
                </span>
                {t.status?.detail && <Tag tone="warn">{t.status.detail}</Tag>}
              </div>
            </div>
            <RowActions>
              <Button variant="secondary" onClick={() => api(`/trunks/${t.id}/reconnect`, { method: 'POST' }).then(() => qc.invalidateQueries({ queryKey: KEY }))}>
                Neu anmelden
              </Button>
              <Button variant="secondary" onClick={() => setEditing(t)}>
                Einstellungen
              </Button>
            </RowActions>
          </div>
          <NumbersTable trunk={t} dirData={dir.data} />
        </section>
      ))}

      <OutboundRulesSection />

      {editing && <TrunkForm key={editing === 'new' ? 'new' : editing.id} trunk={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------- Rufnummern

function NumbersTable({ trunk, dirData }: { trunk: TrunkView; dirData: ReturnType<typeof useDirectory>['data'] }) {
  const [editing, setEditing] = useState<TrunkNumber | 'new' | null>(null);
  const qc = useQueryClient();
  const remove = useMutation({ mutationFn: (id: string) => api(`/numbers/${id}`, { method: 'DELETE' }), onSuccess: () => qc.invalidateQueries({ queryKey: KEY }) });
  return (
    <div style={{ marginTop: 16 }}>
      {trunk.numbers.length === 0 ? (
        <p className={styles.note}>Noch keine Rufnummer. Trage deine easybell-Nummer ein, damit Anrufe ankommen.</p>
      ) : (
        <Table aria-label={`Rufnummern von ${trunk.name}`}>
          <thead>
            <tr>
              <th>Rufnummer</th>
              <th>Bezeichnung</th>
              <th>Wird weitergeleitet an</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {trunk.numbers.map((n) => (
              <tr key={n.id}>
                <th scope="row">{n.number}</th>
                <td>{n.label || <span className={styles.muted}>–</span>}</td>
                <td>{describeDestination(n.destination, dirData)}</td>
                <td>
                  <RowActions>
                    <Button variant="secondary" onClick={() => setEditing(n)}>
                      Ändern
                    </Button>
                    <Button variant="secondary" onClick={() => confirm(`Rufnummer ${n.number} entfernen?`) && remove.mutate(n.id)}>
                      Entfernen
                    </Button>
                  </RowActions>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <div style={{ marginTop: 12 }}>
        <Button variant="secondary" onClick={() => setEditing('new')}>
          Rufnummer hinzufügen
        </Button>
      </div>
      {editing && <NumberForm trunk={trunk} number={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function NumberForm({ trunk, number, onClose }: { trunk: TrunkView; number: TrunkNumber | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [value, setValue] = useState(number?.number ?? '');
  const [label, setLabel] = useState(number?.label ?? '');
  const [destination, setDestination] = useState<Destination>(number?.destination ?? { type: 'hangup' });
  const save = useMutation({
    mutationFn: () => (number ? api(`/numbers/${number.id}`, { method: 'PATCH', body: { label, destination } }) : api(`/trunks/${trunk.id}/numbers`, { method: 'POST', body: { number: value, label, destination } })),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: KEY });
      onClose();
    },
  });
  return (
    <Modal
      open
      onClose={onClose}
      title={number ? `Rufnummer ${number.number}` : 'Rufnummer hinzufügen'}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending || (!number && !value)}>
            Speichern
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        {!number && <Field label="Rufnummer" value={value} onChange={(e) => setValue(e.target.value)} placeholder="+49 371 123456" hint="Mit Vorwahl – wird als +49… gespeichert." autoFocus />}
        <Field label="Bezeichnung" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Hauptnummer" />
        <DestinationPicker label="Anrufe landen bei" value={destination} onChange={setDestination} allow={['extension', 'flow', 'queue', 'voicemail', 'conference', 'hangup']} />
        {save.isError && <Notice tone="error">{errorText(save.error)}</Notice>}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- Anbieter bearbeiten

const EASYBELL = { name: 'easybell', host: 'secure.sip.easybell.de', port: 5061, transport: 'TLS' as const, domain: 'sip.easybell.de', srtp: 'required' as const, maxChannels: 2, numberFormat: 'e164' as const, fromMode: 'number' as const, identityHeader: 'p-preferred' as const, register: true, registerExpires: 300 };

function TrunkForm({ trunk, onClose }: { trunk: TrunkView | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({
    name: trunk?.name ?? '',
    enabled: trunk?.enabled ?? true,
    host: trunk?.host ?? '',
    port: trunk?.port ?? 5061,
    transport: trunk?.transport ?? 'TLS',
    domain: trunk?.domain ?? '',
    username: trunk?.username ?? '',
    authUsername: trunk?.authUsername ?? '',
    password: '',
    register: trunk?.register ?? true,
    registerExpires: trunk?.registerExpires ?? 300,
    srtp: trunk?.srtp ?? 'required',
    maxChannels: trunk?.maxChannels ?? 2,
    numberFormat: trunk?.numberFormat ?? 'e164',
    fromMode: trunk?.fromMode ?? 'number',
    identityHeader: trunk?.identityHeader ?? 'p-preferred',
    defaultCallerId: trunk?.defaultCallerId ?? '',
    allowedSources: (trunk?.allowedSources ?? []).join('\n'),
    verifyCertificate: trunk?.verifyCertificate ?? true,
  });
  const set = <K extends keyof typeof f>(key: K, value: (typeof f)[K]) => setF((p) => ({ ...p, [key]: value }));
  const [confirmDelete, setConfirmDelete] = useState(false);

  const body = () => {
    const { password, allowedSources, authUsername, defaultCallerId, ...rest } = f;
    return { ...rest, authUsername: authUsername || null, defaultCallerId: defaultCallerId || null, allowedSources: allowedSources.split(/[\s,;]+/).filter(Boolean), ...(password ? { password } : {}) };
  };
  const save = useMutation({
    mutationFn: () => (trunk ? api(`/trunks/${trunk.id}`, { method: 'PATCH', body: body() }) : api('/trunks', { method: 'POST', body: body() })),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: KEY });
      onClose();
    },
  });
  const remove = useMutation({
    mutationFn: () => api(`/trunks/${trunk!.id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: KEY });
      onClose();
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={trunk ? `Anbieter ${trunk.name}` : 'Anbieter hinzufügen'}
      description="Zugang zum Telefonnetz, z. B. easybell"
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending || !f.name || !f.host || !f.username || (!trunk && !f.password)}>
            Speichern
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        {!trunk && (
          <div>
            <Button variant="secondary" onClick={() => setF((p) => ({ ...p, ...EASYBELL, authUsername: '' }))}>
              Vorlage easybell übernehmen
            </Button>
            <p className={styles.muted} style={{ marginTop: 6 }}>
              Setzt Server secure.sip.easybell.de, Port 5061 (TLS), SRTP und 2 Leitungen. Benutzername und Passwort stehen in deinem easybell-Kundencenter.
            </p>
          </div>
        )}
        <div className={styles.row}>
          <Field label="Name" value={f.name} onChange={(e) => set('name', e.target.value)} />
          <Checkbox label="Aktiv" checked={f.enabled} onChange={(e) => set('enabled', e.target.checked)} />
        </div>
        <div className={styles.row}>
          <Field label="Server" value={f.host} onChange={(e) => set('host', e.target.value)} placeholder="secure.sip.easybell.de" />
          <Field label="Port" inputMode="numeric" value={String(f.port)} onChange={(e) => set('port', Number(e.target.value.replace(/\D/g, '')) || 0)} />
          <Select label="Übertragung" value={f.transport} onChange={(e) => set('transport', e.target.value as 'TLS' | 'TCP' | 'UDP')}>
            <option value="TLS">TLS (verschlüsselt)</option>
            <option value="TCP">TCP</option>
            <option value="UDP">UDP</option>
          </Select>
          <Field label="SIP-Domain" value={f.domain} onChange={(e) => set('domain', e.target.value)} placeholder="sip.easybell.de" />
        </div>
        <div className={styles.row}>
          <Field label="Benutzername (SIP-ID)" value={f.username} onChange={(e) => set('username', e.target.value)} />
          <Field label={trunk ? 'Neues Passwort (leer = behalten)' : 'Passwort'} type="password" autoComplete="new-password" value={f.password} onChange={(e) => set('password', e.target.value)} />
          <Field label="Abweichender Auth-Benutzer" value={f.authUsername} onChange={(e) => set('authUsername', e.target.value)} hint="Meist leer." />
          <Field label="Standard-Absendernummer" value={f.defaultCallerId} onChange={(e) => set('defaultCallerId', e.target.value)} placeholder="+49371123456" />
        </div>
        <div className={styles.row}>
          <Select label="Sprachverschlüsselung (SRTP)" value={f.srtp} onChange={(e) => set('srtp', e.target.value as 'none' | 'optional' | 'required')}>
            <option value="required">Erforderlich</option>
            <option value="optional">Wenn möglich</option>
            <option value="none">Aus</option>
          </Select>
          <Field label="Gleichzeitige Gespräche" inputMode="numeric" value={String(f.maxChannels)} onChange={(e) => set('maxChannels', Number(e.target.value.replace(/\D/g, '')) || 1)} hint="Bei easybell Call Start: 2." />
          <Select label="Rufnummernformat" value={f.numberFormat} onChange={(e) => set('numberFormat', e.target.value as typeof f.numberFormat)}>
            <option value="e164">+49371…</option>
            <option value="e164-no-plus">49371…</option>
            <option value="national">0371…</option>
            <option value="international-00">0049371…</option>
          </Select>
          <Select label="Absender (From)" value={f.fromMode} onChange={(e) => set('fromMode', e.target.value as 'number' | 'username')}>
            <option value="number">Rufnummer</option>
            <option value="username">Benutzername</option>
          </Select>
          <Select label="Absenderkennung (Identity)" value={f.identityHeader} onChange={(e) => set('identityHeader', e.target.value as typeof f.identityHeader)}>
            <option value="p-preferred">P-Preferred-Identity</option>
            <option value="p-asserted">P-Asserted-Identity</option>
            <option value="none">Keine</option>
          </Select>
          <Field label="Anmeldung erneuern (Sekunden)" inputMode="numeric" value={String(f.registerExpires)} onChange={(e) => set('registerExpires', Number(e.target.value.replace(/\D/g, '')) || 300)} />
        </div>
        <Checkbox label="Beim Anbieter anmelden (Registrierung)" checked={f.register} onChange={(e) => set('register', e.target.checked)} />
        <Checkbox label="Zertifikat des Anbieters prüfen" checked={f.verifyCertificate} onChange={(e) => set('verifyCertificate', e.target.checked)} />
        <TextArea label="Weitere erlaubte Absender-Adressen (eine pro Zeile)" value={f.allowedSources} onChange={(e) => set('allowedSources', e.target.value)} rows={3} hint="Eingehende Anrufe werden nur von der Verbindung zum Anbieter und diesen Adressen angenommen." />
        {save.isError && <Notice tone="error">{errorText(save.error)}</Notice>}
        {trunk && (
          <div>
            {confirmDelete ? (
              <div className={styles.inline}>
                <span>Anbieter samt Rufnummern löschen?</span>
                <Button variant="secondary" onClick={() => remove.mutate()}>
                  Ja, löschen
                </Button>
                <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
                  Nein
                </Button>
              </div>
            ) : (
              <Button variant="secondary" onClick={() => setConfirmDelete(true)}>
                Anbieter löschen …
              </Button>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- Sperrregeln

function OutboundRulesSection() {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ['outbound-rules'], queryFn: () => api<OutboundRules>('/settings/outbound-rules') });
  const [draft, setDraft] = useState<(Omit<OutboundRules, 'allowedCountries' | 'blockedPrefixes'> & { allowedCountries: string; blockedPrefixes: string }) | null>(null);
  const rules = query.data;
  const current = draft ?? (rules ? { ...rules, allowedCountries: rules.allowedCountries.join(', '), blockedPrefixes: rules.blockedPrefixes.join(', ') } : null);
  const save = useMutation({
    mutationFn: () => {
      const d = current!;
      return api('/settings/outbound-rules', { method: 'PUT', body: { ...d, allowedCountries: d.allowedCountries.split(/[\s,;]+/).filter(Boolean), blockedPrefixes: d.blockedPrefixes.split(/[\s,;]+/).filter(Boolean) } });
    },
    onSuccess: async () => {
      setDraft(null);
      await qc.invalidateQueries({ queryKey: ['outbound-rules'] });
    },
  });
  if (!current) return null;
  const set = (patch: Partial<typeof current>) => setDraft({ ...current, ...patch });
  return (
    <section className={styles.section}>
      <h2 className={styles.heading}>Sperrregeln für ausgehende Anrufe</h2>
      <p className={styles.note} style={{ marginBottom: 16 }}>
        Schützt vor teuren Verbindungen. Notrufe (110, 112) sind immer erlaubt.
      </p>
      <div className={styles.form}>
        <Checkbox label="Anrufe ins Ausland erlauben" checked={current.allowInternational} onChange={(e) => set({ allowInternational: e.target.checked })} />
        {current.allowInternational && <Field label="Erlaubte Länder (Vorwahlen, leer = alle)" value={current.allowedCountries} onChange={(e) => set({ allowedCountries: e.target.value })} placeholder="+43, +41" />}
        <Checkbox label="Mehrwertdienste (0900, 0137 …) erlauben" checked={current.allowPremium} onChange={(e) => set({ allowPremium: e.target.checked })} />
        <Checkbox label="Servicenummern (0180 …) erlauben" checked={current.allowService} onChange={(e) => set({ allowService: e.target.checked })} />
        <Field label="Zusätzlich gesperrte Vorwahlen" value={current.blockedPrefixes} onChange={(e) => set({ blockedPrefixes: e.target.value })} placeholder="+49180, +4990" />
        <Field label="Amtsholung (Ziffer vor externen Nummern)" value={current.outsidePrefix} onChange={(e) => set({ outsidePrefix: e.target.value.replace(/\D/g, '').slice(0, 2) })} hint="Leer = nicht nötig. Mit „0“ wählst du extern über 0 + Nummer." style={{ maxWidth: 320 }} />
        {save.isError && <Notice tone="error">{errorText(save.error)}</Notice>}
        {save.isSuccess && !draft && <Notice tone="success">Gespeichert.</Notice>}
        <div>
          <Button onClick={() => save.mutate()} disabled={!draft || save.isPending}>
            Regeln speichern
          </Button>
        </div>
      </div>
    </section>
  );
}
